import { createContext, useContext, useEffect, useState } from "react";
import { Loader } from "../components/loader";

import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";


const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState({ user: null, token: null, role: null });
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [commonError, setCommonError] = useState(null);
  const [commonWarning, setCommonWarning] = useState(null);
  const [commonSuccess, setCommonSuccess] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [selectedLang, setSelectedLang] = useState({ code: "en", name: "English", flag: "🇬🇧" });


  // Notification handler
  useEffect(() => {
    const showToast = (message, icon) => {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon,
        title: message,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    };

    if (commonError) {
      showToast(commonError, "error");
      setTimeout(() => setCommonError(null), 2000);
    }
    if (commonWarning) {
      showToast(commonWarning, "warning");
      setTimeout(() => setCommonWarning(null), 2000);
    }
    if (commonSuccess) {
      showToast(commonSuccess, "success");
      setTimeout(() => setCommonSuccess(null), 2000);
    }
  }, [commonError, commonWarning, commonSuccess]);

  // Load auth from localStorage on mount
  useEffect(() => {
    setIsLoading(true);
    try {
      const storedAuth = localStorage.getItem("authData");
      if (storedAuth) {
        setAuth(JSON.parse(storedAuth));
      }

      const storedBreadcrumbs = localStorage.getItem("breadcrumbs");
      if (storedBreadcrumbs) {
        setBreadcrumbs(JSON.parse(storedBreadcrumbs));
      }
    } catch (err) {
      setCommonError("Failed to parse stored data.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    try {
      if (auth.user && auth.token) {
        localStorage.setItem("authData", JSON.stringify(auth));
      } else {
        localStorage.removeItem("authData");
      }
    } catch (err) {
      setCommonError("Error saving auth data.");
    }
  }, [auth]);

  useEffect(() => {
    try {
      if (breadcrumbs.length > 0) {
        localStorage.setItem("breadcrumbs", JSON.stringify(breadcrumbs));
      } else {
        localStorage.removeItem("breadcrumbs");
      }
    } catch (err) {
      setCommonError("Error saving breadcrumbs.");
    }
  }, [breadcrumbs]);

  const login = (userData, token) => {

    setAuth({
      user: userData,
      token,
      role: userData?.role || "user",
    });

    window.location.href = "/";
  };
  const logout = () => {
    setAuth({
      user: null,
      token: null,
      role: null,
    });

    localStorage.removeItem("authData");
    localStorage.removeItem("breadcrumbs");

    navigate("/login");
  };

  return (
    <AuthContext.Provider
      value={{
        auth,
        login,
        logout,
        isLoading,
        setCommonError,
        setCommonWarning,
        setCommonSuccess,
        breadcrumbs,
        setBreadcrumbs,
        selectedLang,
        setSelectedLang,
      }}
    >
      <ToastContainer />
      {isLoading ? <Loader /> : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
